import { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { PHASES, GENDER_QUESTION, AGE_QUESTION, LIFE_STAGE_QUESTION } from "../data/assessment";
import { ageQuestionForGender } from "../config/landing";
import { useT } from "../i18n";
import { API } from "../App";
import { useQuestionOverrides, resolvePhase } from "../hooks/useQuestionOverrides";
import LanguageToggle from "../components/LanguageToggle";
import ProgressBar from "../components/ProgressBar";
import QuestionCard from "../components/QuestionCard";
import InsightScreen from "../components/InsightScreen";
import MaleAssessment from "./MaleAssessment";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import GoogleAuthButton from "../components/GoogleAuthButton";
import { Checkbox } from "../components/ui/checkbox";
import { Button } from "../components/ui/button";
import { ShieldCheck } from "lucide-react";

const TOTAL_PHASES = PHASES.length; // 9 real phases; gender is a personalization step

export default function Assessment({ lang, setLang, languages }) {
  const tr = useT(lang);
  const nav = useNavigate();
  const textOverrides = useQuestionOverrides();
  // Salon referral — captured from ?ref=<slug> in the URL query and preserved
  // through the entire flow so the completed assessment attributes to the salon.
  const salonRef = (typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("ref")) || "";
  // Salon pre-quiz prefill — captured on the salon dashboard before launch.
  // Stored in localStorage so it survives the popup tab isolation caused by
  // noopener when the salon dashboard launches the client quiz in a new tab.
  const salonPrefill = (() => {
    if (typeof window === "undefined" || !salonRef) return null;
    try {
      const raw = localStorage.getItem("kt_salon_prequiz");
      if (!raw) return null;
      const p = JSON.parse(raw);
      // TTL 30 minutes — avoids stale data lingering across sessions.
      if (p?.captured_at) {
        const ageMs = Date.now() - new Date(p.captured_at).getTime();
        if (Number.isFinite(ageMs) && ageMs > 30 * 60 * 1000) {
          try { localStorage.removeItem("kt_salon_prequiz"); } catch {}
          return null;
        }
      }
      return (p && p.first_name && p.email && p.consent) ? p : null;
    } catch { return null; }
  })();
  // Auto-save & resume: persist every answer to localStorage so the client
  // can leave the page, refresh, close the tab, lose connectivity, or come
  // back later — and continue from the last successfully saved question.
  // 30-day freshness window: older saves are ignored so returning users don't
  // get stuck in a stale flow. Cleared on successful submit.
  const QUIZ_KEY = "kt_quiz_state_female_v1";
  const QUIZ_TTL_MS = 30 * 24 * 60 * 60 * 1000;
  const restored = (() => {
    if (typeof window === "undefined") return null;
    try {
      const raw = localStorage.getItem(QUIZ_KEY);
      if (!raw) return null;
      const p = JSON.parse(raw);
      if (!p || typeof p !== "object") return null;
      if (p.saved_at && Date.now() - p.saved_at > QUIZ_TTL_MS) {
        localStorage.removeItem(QUIZ_KEY);
        return null;
      }
      return p;
    } catch { return null; }
  })();
  const [showGender, setShowGender] = useState(restored?.showGender ?? true);
  const [showAge, setShowAge] = useState(restored?.showAge ?? false);
  const [showLifeStage, setShowLifeStage] = useState(restored?.showLifeStage ?? false);
  const [ageBand, setAgeBand] = useState(restored?.ageBand ?? "");
  const [lifeStage, setLifeStage] = useState(restored?.lifeStage ?? "");
  const [genderChoice, setGenderChoice] = useState(restored?.genderChoice ?? null);
  const [phaseIdx, setPhaseIdx] = useState(restored?.phaseIdx ?? 0);
  const [qIdx, setQIdx] = useState(restored?.qIdx ?? 0);
  const [answers, setAnswers] = useState(restored?.answers ?? {});
  const [showInsight, setShowInsight] = useState(restored?.showInsight ?? false);
  const [emailForm, setEmailForm] = useState(restored?.emailForm ?? false);
  const [name, setName] = useState(restored?.name ?? (salonPrefill?.first_name || ""));
  const [email, setEmail] = useState(restored?.email ?? (salonPrefill?.email || ""));
  const [consent, setConsent] = useState(restored?.consent ?? !!salonPrefill?.consent);
  const [submitting, setSubmitting] = useState(false);

  // Persist quiz state on every relevant change so it survives refresh/close/
  // OAuth round-trip. Cleared on successful submission.
  useEffect(() => {
    try {
      localStorage.setItem(QUIZ_KEY, JSON.stringify({
        showGender, showAge, showLifeStage, ageBand, lifeStage, genderChoice,
        phaseIdx, qIdx, answers, showInsight, emailForm, name, email, consent,
        saved_at: Date.now(),
      }));
    } catch {}
  }, [showGender, showAge, showLifeStage, ageBand, lifeStage, genderChoice,
      phaseIdx, qIdx, answers, showInsight, emailForm, name, email, consent]);

  const phase = PHASES[phaseIdx];
  const currentQ = phase?.questions[qIdx];
  const answered = answers[currentQ?.id];

  const selectOption = (optId) => {
    setAnswers((a) => ({ ...a, [currentQ.id]: optId }));
    setTimeout(() => goNext(), 260);
  };

  const selectGender = (optId) => {
    setAnswers((a) => ({ ...a, [GENDER_QUESTION.id]: optId }));
    // Both paths now flow through Age → (Life-stage if female) → questionnaire.
    setTimeout(() => {
      setShowGender(false);
      setShowAge(true);
    }, 260);
  };

  const selectAge = (optId) => {
    setAgeBand(optId);
    setTimeout(() => {
      setShowAge(false);
      // Male path skips the life-stage screen (per user spec).
      if (answers[GENDER_QUESTION.id] === "man") {
        setGenderChoice("man");
      } else {
        setShowLifeStage(true);
      }
    }, 260);
  };

  const selectLifeStage = (optId) => {
    setLifeStage(optId);
    setTimeout(() => setShowLifeStage(false), 260);
  };

  const submitPrefilled = async (finalAnswers) => {
    if (!salonPrefill) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/assessments`, {
        first_name: salonPrefill.first_name,
        email: salonPrefill.email,
        marketing_consent: false,
        marketing_wording_version: "v1",
        age_band: ageBand,
        life_stage: lifeStage || "none",
        salon_ref: salonRef,
        language: lang,
        answers: finalAnswers,
      });
      try { localStorage.removeItem("kt_salon_prequiz"); } catch {}
      try { localStorage.removeItem(QUIZ_KEY); } catch {}
      nav(`/results/${res.data.result_id}`);
    } catch (e) {
      toast.error(lang === "it" ? "Errore nell'invio. Riprova." : "Something went wrong. Please try again.");
      setSubmitting(false);
    }
  };

  const goNext = () => {
    if (qIdx < phase.questions.length - 1) setQIdx(qIdx + 1);
    else if (phaseIdx < PHASES.length - 1) setShowInsight(true);
    else if (salonPrefill) submitPrefilled(answers);
    else setEmailForm(true);
  };

  const goBack = () => {
    if (qIdx > 0) setQIdx(qIdx - 1);
    else if (phaseIdx > 0) {
      const prev = PHASES[phaseIdx - 1];
      setPhaseIdx(phaseIdx - 1);
      setQIdx(prev.questions.length - 1);
    } else if (!showAge && !showGender && !showLifeStage) {
      // back from phase 1 → life-stage screen
      setShowLifeStage(true);
    } else if (showLifeStage) {
      setShowLifeStage(false); setShowAge(true);
    } else if (showAge) {
      setShowAge(false); setShowGender(true);
    } else {
      setShowGender(true);
    }
  };

  const dismissInsight = () => {
    setShowInsight(false);
    setPhaseIdx(phaseIdx + 1);
    setQIdx(0);
  };

  const submit = async () => {
    if (!name.trim() || !email.trim()) {
      toast.error(lang === "it" ? "Nome ed email obbligatori" : "Name and email are required");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/assessments`, {
        first_name: name.trim(),
        email: email.trim(),
        marketing_consent: consent,
        marketing_wording_version: "v1",
        age_band: ageBand,
        life_stage: lifeStage || "none",
        salon_ref: salonRef,
        language: lang,
        answers,   // gender_for_visual is stripped server-side
      });
      try { localStorage.removeItem(QUIZ_KEY); } catch {}
      nav(`/results/${res.data.result_id}`);
    } catch (e) {
      toast.error(lang === "it" ? "Errore nell'invio. Riprova." : "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white relative">
      {genderChoice === "man" ? (
        <MaleAssessment lang={lang} setLang={setLang} languages={languages} genderAnswer="man" ageBand={ageBand} salonRef={salonRef} />
      ) : (
        <>
      <ProgressBar
        phaseIndex={(showGender || showAge || showLifeStage) ? 0 : phaseIdx}
        totalPhases={TOTAL_PHASES}
        questionIndex={(showGender || showAge || showLifeStage) ? 0 : qIdx}
        totalInPhase={(showGender || showAge || showLifeStage) ? 1 : (phase?.questions.length || 1)}
        personalizing={showGender || showAge || showLifeStage}
      />

      <div className="fixed top-3 right-4 z-40">
        <LanguageToggle lang={lang} setLang={setLang} languages={languages} />
      </div>

      <div className="max-w-md mx-auto px-6 pt-24 pb-40">
        {!emailForm && showGender && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55 }}
            data-testid="gender-question"
          >
            <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66] mb-6">
              {lang === "it" ? "Solo per personalizzare le immagini" : "Only to personalize photographs"}
            </div>
            <QuestionCard q={GENDER_QUESTION} lang={lang} selected={answers[GENDER_QUESTION.id]} onSelect={selectGender} />
          </motion.div>
        )}

        {!emailForm && showAge && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55 }}
            data-testid="age-question"
          >
            <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66] mb-6">
              {lang === "it" ? "A proposito di te" : "About you"}
            </div>
            <QuestionCard
              q={ageQuestionForGender(AGE_QUESTION, answers[GENDER_QUESTION.id])}
              lang={lang}
              selected={ageBand}
              onSelect={selectAge}
            />
          </motion.div>
        )}

        {!emailForm && showLifeStage && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55 }}
            data-testid="life-stage-question"
          >
            <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66] mb-6">
              {lang === "it" ? "Modalità routine delicata" : "Gentle routine mode"}
            </div>
            <QuestionCard q={LIFE_STAGE_QUESTION} lang={lang} selected={lifeStage} onSelect={selectLifeStage} />
          </motion.div>
        )}

        {!emailForm && !showGender && !showAge && !showLifeStage && (
          <>
            <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66] mb-6">
              {phase ? resolvePhase(textOverrides, phase.id, "title", lang, phase.title) : ""}
            </div>
            <AnimatePresence mode="wait">
              <QuestionCard key={currentQ?.id} q={currentQ} lang={lang} selected={answered} onSelect={selectOption} />
            </AnimatePresence>
          </>
        )}

        {emailForm && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            data-testid="email-form"
          >
            <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66] mb-4">— {tr.email_kicker}</div>
            <h2 className="font-serif text-4xl leading-tight text-[#383633] tracking-tight mb-4">
              {tr.email_title}
            </h2>
            <p className="text-[#6F6B66] mb-8">{tr.email_sub}</p>

            {/* Emergent-managed Google sign-in — prefill only. Skips typing
                name + email; GDPR consent is still an explicit tick below. */}
            <div className="mb-6" data-testid="email-form-google">
              <GoogleAuthButton
                lang={lang}
                variant="light"
                testid="assessment-google-btn"
                onSuccess={({ email: gEmail, name: gName }) => {
                  if (gEmail) setEmail(gEmail);
                  if (gName) setName(gName.split(" ")[0] || gName);
                  toast.success(
                    lang === "it"
                      ? "Account Google collegato — spunta il consenso e scopri i risultati."
                      : "Google account linked — tick consent and unlock your results."
                  );
                }}
              />
              <div className="mt-4 flex items-center gap-3 text-[10px] kt-tracking-wider uppercase text-[#8A857E]">
                <div className="flex-1 h-px bg-[#ECE7DF]" />
                <span>{lang === "it" ? "oppure inserisci l'email" : "or use email"}</span>
                <div className="flex-1 h-px bg-[#ECE7DF]" />
              </div>
            </div>

            <div className="space-y-5">
              <div>
                <Label htmlFor="name" className="text-xs kt-tracking uppercase text-[#6F6B66]">{tr.first_name}</Label>
                <Input id="name" data-testid="input-name" value={name} onChange={(e) => setName(e.target.value)}
                  className="mt-2 border-[#ECE7DF] bg-white rounded-xl h-12 focus-visible:ring-[#383633]" />
              </div>
              <div>
                <Label htmlFor="email" className="text-xs kt-tracking uppercase text-[#6F6B66]">{tr.email}</Label>
                <Input id="email" type="email" data-testid="input-email" value={email} onChange={(e) => setEmail(e.target.value)}
                  className="mt-2 border-[#ECE7DF] bg-white rounded-xl h-12 focus-visible:ring-[#383633]" />
                <p className="mt-2 text-[11px] text-[#6F6B66] flex items-start gap-1.5">
                  <ShieldCheck className="h-3 w-3 mt-0.5 shrink-0" /> {tr.privacy_short}
                </p>
              </div>
              <label className="flex items-start gap-3 cursor-pointer" data-testid="consent-row">
                <Checkbox id="consent" checked={consent} onCheckedChange={(v) => setConsent(!!v)}
                  className="border-[#6F6B66] mt-1 data-[state=checked]:bg-[#383633] data-[state=checked]:border-[#383633]"
                  data-testid="input-consent" />
                <span className="text-xs text-[#6F6B66] leading-relaxed">{tr.consent}</span>
              </label>
              <Button
                onClick={submit}
                disabled={submitting}
                data-testid="submit-assessment"
                className="w-full h-14 rounded-full bg-[#383633] hover:bg-[#4A4238] text-white text-xs kt-tracking uppercase"
              >
                {submitting ? tr.submitting : tr.submit}
              </Button>
            </div>
          </motion.div>
        )}

        {!emailForm && (
          <div className="mt-10 flex justify-start">
            {(qIdx > 0 || phaseIdx > 0 || !showGender) && (
              <button onClick={goBack} data-testid="back-btn"
                className="text-xs kt-tracking uppercase text-[#6F6B66] hover:text-[#383633] transition">
                ← {tr.back}
              </button>
            )}
          </div>
        )}
      </div>

      <AnimatePresence>
        {showInsight && phase && (
          <InsightScreen phase={phase} lang={lang} onContinue={dismissInsight} continueLabel={tr.continue} />
        )}
      </AnimatePresence>
        </>
      )}
    </div>
  );
}
